/**
 * Parsing/validation for merging an incremental "Buscar mais dados"
 * Section_Event into the already-rendered report (task #21220).
 *
 * Kept as pure functions (no React, no store access) so the parsing contract
 * is unit-testable directly — mirrors `sectionPersist.helper.ts`'s split
 * between decision logic (here) and the actual store/network side effects
 * (left to the caller, `PendingReportSockets.tsx`).
 */
import { SectionEvent } from "~/types/section-streaming";

export interface IncrementalSectionPayload {
  title: string;
  subsection?: string;
  data: Record<string, any>[];
}

/**
 * Narrow `event.section` into a mergeable shape. Returns `null` for anything
 * not eligible: `event.incremental !== true` (the ephemeral initial-preview
 * event, which must never touch the rendered report), or a missing/malformed
 * `section` payload (no `title`, or `data` not an array).
 */
export function extractIncrementalSectionPayload(
  event: SectionEvent,
): IncrementalSectionPayload | null {
  if (event.incremental !== true) {
    return null;
  }
  const section = event.section;
  if (!section || typeof section !== "object") {
    return null;
  }
  const { title, subsection, data } = section as Record<string, unknown>;
  if (typeof title !== "string" || title.length === 0) {
    return null;
  }
  if (!Array.isArray(data)) {
    return null;
  }
  return {
    title,
    subsection: typeof subsection === "string" ? subsection : undefined,
    data: data as Record<string, any>[],
  };
}

/**
 * Canonical `rs_id` from a `section_id` in `"{rs_id}:{network}"` format
 * (ADR-014), or `null` for a malformed/legacy (`"{network}/{title}"`) id.
 */
export function extractRsIdFromSectionId(sectionId: string): string | null {
  const [rsId] = sectionId.split(":");
  return rsId && rsId.length > 0 ? rsId : null;
}
